import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NavLink, Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, Sparkles, Pill, AlertTriangle, ClipboardList, LogOut, Users, Baby, Clock, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

const TILES = [
  { to: "/app/planning",     label: "Presentielijst", desc: "Kinderen aanwezig/afwezig melden, breng- en haaltijden", icon: CalendarDays, color: "sage" },
  { to: "/app/planning",     label: "Dagplanning",    desc: "Vandaag op de groep — moment voor moment", icon: ClipboardList, color: "sage" },
  { to: "/app/activiteiten", label: "Activiteiten",   desc: "Activiteiten uit de bibliotheek — inplannen en registreren", icon: Sparkles, color: "sage" },
  { to: "/app/communicatie", label: "Schriftje",      desc: "AI-schriftje met microfoon — spreek in, verhaal ontstaat", icon: BookOpen, color: "sage" },
  { to: "/app/medicatie",    label: "Medicatie",      desc: "Toedieningen registreren — met individuele PIN-bevestiging", icon: Pill, color: "amber" },
  { to: "/app/incidenten",   label: "Incidentenlijst", desc: "Incidenten registreren — met individuele PIN-bevestiging", icon: AlertTriangle, color: "terracotta" },
];

function TodaySummary({ groupId }) {
  const [state, setState] = useState({ loading: true, present: 0, verwacht: 0, medicatie: 0, activiteiten: 0 });

  useEffect(() => {
    if (!groupId) return;
    const today = new Date().toISOString().slice(0, 10);
    const load = async () => {
      try {
        const [dayRes, medRes] = await Promise.all([
          api.get(`/planning/day?datum=${today}&group_id=${groupId}`).catch(() => ({ data: { items: [] } })),
          api.get(`/medicatieformulieren`).catch(() => ({ data: { items: [] } })),
        ]);
        const items = dayRes.data?.items || [];
        const verwacht = items.length;
        const present = items.filter(i => (i.status || "").toLowerCase() === "aanwezig").length;
        // Actieve medicatieformulieren voor vandaag
        const meds = (medRes.data?.items || []).filter(m => {
          const start = m.start_datum, eind = m.eind_datum, status = (m.status || "").toLowerCase();
          return status === "goedgekeurd" && (!start || start <= today) && (!eind || eind >= today);
        });
        setState({ loading: false, present, verwacht, medicatie: meds.length, activiteiten: 0 });
      } catch {
        setState({ loading: false, present: 0, verwacht: 0, medicatie: 0, activiteiten: 0 });
      }
    };
    load();
  }, [groupId]);

  const stats = [
    { label: "Verwacht vandaag", value: state.loading ? "…" : state.verwacht, icon: Baby, color: "var(--sage)", bg: "var(--sage-100)" },
    { label: "Aanwezig", value: state.loading ? "…" : state.present, icon: Users, color: "#0F766E", bg: "#CCFBF1" },
    { label: "Medicatie actief", value: state.loading ? "…" : state.medicatie, icon: Pill, color: "#B45309", bg: "#FEF3C7" },
    { label: "Tijd", value: new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }), icon: Clock, color: "#64748B", bg: "#F1F5F9" },
  ];

  return (
    <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="group-today-summary">
      {stats.map(s => (
        <div key={s.label} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center gap-3" data-testid={`today-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: s.bg }}>
            <s.icon className="h-5 w-5" style={{ color: s.color }} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-2xl font-display font-semibold leading-none text-neutral-900">{s.value}</div>
            <div className="text-[11px] text-neutral-500 mt-1 truncate">{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GroupDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [groupInfo, setGroupInfo] = useState(null);

  useEffect(() => {
    if (user?.bound_group_id) {
      api.get(`/groups?location_id=${user.bound_location_id || ""}`).then(res => {
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        const g = items.find(x => x.id === user.bound_group_id);
        setGroupInfo(g);
      }).catch(() => {});
    }
  }, [user]);

  // Access-guard
  if (user && user.role !== "groep_account") {
    return <Navigate to="/app" replace />;
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[color:var(--bone)]">
      {/* Topbar */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10" data-testid="group-topbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[color:var(--sage)] flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-tight" data-testid="group-account-name">{user.name}</div>
              <div className="text-xs text-neutral-500">
                {groupInfo?.name && <span>{groupInfo.name}</span>}
                {groupInfo?.name && <span className="mx-1">·</span>}
                <span>Groepslogin · gedeeld account</span>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="group-logout"
          >
            <LogOut className="h-4 w-4 mr-2" /> Uitloggen
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="group-dashboard">
        <div className="mb-6">
          <h1 className="font-display text-3xl tracking-tight text-neutral-900">Welkom, {user.name}</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Dit is jullie groeps-dashboard. Alleen deze 5 onderdelen zijn beschikbaar via dit account.
            Voor persoonlijk rooster of inklok: gebruik de eigen medewerker-login.
          </p>
        </div>

        <TodaySummary groupId={user.bound_group_id} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="group-tiles">
          {TILES.map((t, i) => {
            const bg = t.color === "sage" ? "var(--sage-100)"
              : t.color === "amber" ? "#FEF3C7"
              : "#FDE4D1";
            const iconColor = t.color === "sage" ? "var(--sage)"
              : t.color === "amber" ? "#B45309"
              : "var(--terracotta)";
            return (
              <NavLink
                key={t.label}
                to={t.to}
                data-testid={`group-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block bg-white border border-neutral-200 rounded-xl p-6 hover:border-[color:var(--sage)] transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: bg }}>
                  <t.icon className="h-6 w-6" style={{ color: iconColor }} strokeWidth={2} />
                </div>
                <div className="font-display text-lg font-semibold text-neutral-900 group-hover:text-[color:var(--sage)] transition-colors">
                  {t.label}
                </div>
                <p className="text-sm text-neutral-500 mt-2">{t.desc}</p>
              </NavLink>
            );
          })}
        </div>

        <div className="mt-10 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="group-security-notice">
          <div className="font-semibold mb-1">🔒 Delen van dit account</div>
          Dit is een gedeeld groepsaccount. Bij het registreren van medicatie of incidenten moet je altijd
          jouw persoonlijke <strong>6-cijferige PIN</strong> invoeren, zodat duidelijk blijft wie de handeling deed.
          Je stelt jouw PIN in via je persoonlijke medewerker-login.
        </div>
      </main>
    </div>
  );
}
